import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, GripVertical, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const ShopAdvantagesManager = () => {
  const [advantages, setAdvantages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAdvantage, setEditingAdvantage] = useState(null);
  const [formData, setFormData] = useState({
    icon: '✓',
    title: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchAdvantages();
  }, []);

  const fetchAdvantages = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/shop-advantages');
      setAdvantages(response.data);
    } catch (error) {
      console.error('Error fetching shop advantages:', error);
      toast.error('Fehler beim Laden der Shop-Vorteile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAdvantage) {
        await axiosInstance.put(`/admin/shop-advantages/${editingAdvantage.id}`, formData);
        toast.success('Shop-Vorteil aktualisiert!');
      } else {
        await axiosInstance.post('/admin/shop-advantages', formData);
        toast.success('Shop-Vorteil erstellt!');
      }
      
      fetchAdvantages();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving shop advantage:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (advantageId) => {
    if (!window.confirm('Möchten Sie diesen Shop-Vorteil wirklich löschen?')) return;
    
    try {
      await axiosInstance.delete(`/admin/shop-advantages/${advantageId}`);
      toast.success('Shop-Vorteil gelöscht');
      fetchAdvantages();
    } catch (error) {
      console.error('Error deleting shop advantage:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleToggleActive = async (advantage) => {
    try {
      await axiosInstance.put(`/admin/shop-advantages/${advantage.id}`, {
        is_active: !advantage.is_active
      });
      toast.success(advantage.is_active ? 'Deaktiviert' : 'Aktiviert');
      fetchAdvantages();
    } catch (error) {
      console.error('Error toggling advantage:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleEdit = (advantage) => {
    setEditingAdvantage(advantage);
    setFormData({
      icon: advantage.icon,
      title: advantage.title,
      description: advantage.description || '',
      is_active: advantage.is_active
    });
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingAdvantage(null);
    setFormData({
      icon: '✓',
      title: '',
      description: '',
      is_active: true
    });
  };

  const commonEmojis = ['✓', '✔️', '🚚', '💎', '⭐', '🎁', '💳', '🏆', '🔒', '📦', '🌟', '❤️'];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Shop Vorteile</CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Zeigen Sie Ihren Kunden die Vorteile Ihres Shops
            </p>
          </div>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Neuer Vorteil
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">Lädt...</div>
        ) : advantages.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="mb-4">Noch keine Shop-Vorteile erstellt</p>
            <Button onClick={() => setShowDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Ersten Vorteil erstellen
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {advantages.map((advantage) => (
              <div
                key={advantage.id}
                className={`p-4 rounded-lg border-2 transition-all ${
                  advantage.is_active
                    ? 'bg-white border-gray-200'
                    : 'bg-gray-50 border-gray-300 opacity-60'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="text-3xl">{advantage.icon}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm mb-1">{advantage.title}</h4>
                    {advantage.description && (
                      <p className="text-xs text-gray-600">{advantage.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(advantage)}
                      title={advantage.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {advantage.is_active ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(advantage)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(advantage.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAdvantage ? 'Shop-Vorteil bearbeiten' : 'Neuer Shop-Vorteil'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Icon / Emoji</Label>
              <div className="flex gap-2 items-center mt-2">
                <Input
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-20 text-center text-2xl"
                  maxLength={2}
                  required
                />
                <div className="flex-1">
                  <div className="flex flex-wrap gap-1">
                    {commonEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon: emoji })}
                        className="w-8 h-8 text-xl hover:bg-gray-100 rounded border"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Oder geben Sie ein beliebiges Emoji ein
              </p>
            </div>

            <div>
              <Label>Titel *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="z.B. Schneller Versand"
                required
              />
            </div>

            <div>
              <Label>Beschreibung (optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="z.B. 2-3 Werktage"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Auf der Webseite anzeigen
              </Label>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Abbrechen
              </Button>
              <Button type="submit">
                {editingAdvantage ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ShopAdvantagesManager;
